import type { DisposalSet } from '@neptune/cx/utils';
import { focusRichTextField } from './rich-text';

const HIGHLIGHT_CLASS = 'chroa-discussion-board__message--highlighted';

/** Matches the animation in styles.scss, so the outline is gone before the class is. */
const HIGHLIGHT_MS = 3000;

/**
 * The card is what gets highlighted and focused. The anchor carries the message's fragment name and
 * is where the page scrolls to, offset in styles.scss to clear the admin bar.
 */
interface LinkedMessage {
    anchor: HTMLElement;
    card: HTMLElement;
}

const findMessage = (root: HTMLElement, messageId: string): LinkedMessage | null => {
    const escapedId = CSS.escape(messageId);
    const anchor = root.querySelector<HTMLElement>(`[data-chroa-message-anchor="${escapedId}"]`);
    const card = root.querySelector<HTMLElement>(`[data-chroa-message-id="${escapedId}"]`);

    return anchor && card ? { anchor, card } : null;
};

/**
 * Finds the message this page was opened to show.
 *
 * A link from an email carries no URL fragment -- the message is named by a portlet parameter
 * the server resolves -- so the attribute is the primary source and the fragment only a
 * fallback. A fragment is honoured only when it names a message, which leaves the board and
 * answers-list anchors to discussion:anchor.
 */
const resolveTarget = (root: HTMLElement): LinkedMessage | null => {
    const targetId = root.dataset.chroaTargetMessage;

    if (targetId) {
        const named = findMessage(root, targetId);

        // Falls through rather than giving up: the named message may have been deleted since the
        // link was sent, and a fragment in the URL is still worth honouring.
        if (named) {
            return named;
        }
    }

    const hash = location.hash.slice(1);

    if (!hash) {
        return null;
    }

    // By attribute, not id: on a full page load an anchor takes its fragment as its id only once
    // the page has loaded, after this has run.
    const hashEl = root.querySelector<HTMLElement>(`[data-chroa-fragment="${CSS.escape(hash)}"]`);
    const hashMessageId = hashEl?.dataset.chroaMessageAnchor;

    return hashMessageId ? findMessage(root, hashMessageId) : null;
};

/**
 * Whether the visitor followed a link here. Back, forward and reload are left to the browser's own
 * scroll restoration, which the message would otherwise pull them away from.
 */
const isFreshNavigation = (): boolean => {
    const [navigation] = performance.getEntriesByType(
        'navigation',
    ) as PerformanceNavigationTiming[];

    return !navigation || navigation.type === 'navigate';
};

/**
 * Finds the body field of the composer the server left open.
 *
 * Absent whenever nothing invited a reply, and also whenever the visitor may not post -- the
 * composer is not rendered at all then, which is why a miss is ordinary rather than an error.
 */
const resolveReplyField = (root: HTMLElement): HTMLElement | null => {
    const replyParentId = root.dataset.chroaReplyOpen;

    if (!replyParentId) {
        return null;
    }

    const form = root.querySelector<HTMLElement>(
        `[data-chroa-reply-form="${CSS.escape(replyParentId)}"]`,
    );

    // Deliberately not gated on the editor having initialised: this runs after the editors are
    // set up, but tying the caret to that ordering is what silently drops it when anything
    // upstream changes. focusRichTextField copes with a field at any stage.
    return form?.querySelector<HTMLElement>('[data-chroa-rich-text]') ?? null;
};

export const initDeepLink = (root: HTMLElement, gc: DisposalSet): void => {
    // Re-runs on every SPA navigation, so any highlight left over from the last render has to
    // go -- including one on this same element, which also restarts its animation below.
    for (const highlighted of root.querySelectorAll(`.${HIGHLIGHT_CLASS}`)) {
        highlighted.classList.remove(HIGHLIGHT_CLASS);
    }

    if (!isFreshNavigation()) {
        return;
    }

    const target = resolveTarget(root);

    if (!target) {
        return;
    }

    const { anchor, card } = target;

    // A fresh page is still settling, and animating across it reads as a lurch. Within a
    // navigation the scroll is short and continuity is worth more.
    const behavior: ScrollBehavior = document.readyState === 'complete' ? 'smooth' : 'auto';

    // Scrolling again would undo any scroll the visitor has made since.
    if (!anchor.hasAttribute('data-chroa-landed')) {
        window.scrollTo({
            behavior,
            top: anchor.getBoundingClientRect().top + window.scrollY,
        });
    }

    // Announces the move to a screen reader and puts the next Tab inside the message.
    // The landing or the scroll above owns the position, so neither branch may scroll.
    const replyField = resolveReplyField(root);

    if (!replyField || !focusRichTextField(replyField)) {
        card.focus({ preventScroll: true });
    }

    if (root.dataset.chroaTargetHighlight !== 'false') {
        void card.offsetWidth;

        card.classList.add(HIGHLIGHT_CLASS);

        const timer = window.setTimeout(() => {
            card.classList.remove(HIGHLIGHT_CLASS);
        }, HIGHLIGHT_MS);

        gc.register(() => window.clearTimeout(timer));
    }

    // Names the message in the address bar, so that a copy of the URL lands on it too.
    // replaceState, not pushState: nobody clicked to get here, so Back should leave the page
    // rather than undo a scroll. The existing state is carried over because Liferay SPA keeps its
    // own marker there and falls back to a full reload on popstate without it.
    const fragment = `#${anchor.dataset.chroaFragment}`;

    if (location.hash !== fragment) {
        history.replaceState(history.state, '', fragment);
    }
};
